import { useThumbnail } from "@/features/coach-review/hooks/useThumbnail";
import { ClipboardList } from "lucide-react";

/** A note's display title — the observation-aware fallback chain.
 * Re-exported from `feedbackHelpers` (the tested source of truth)
 * so card components can keep importing it from here. */
export { noteDisplayTitle as noteTitle } from "../feedbackHelpers";

/**
 * Small shared pieces for the `/feedback` viewer cards — the
 * access-controlled thumbnail tile and the tone pill.
 *
 * Both port directly from legacy `js/coaching/feedback.js`:
 *   - `FeedbackThumb` wraps the M5 `useThumbnail` primitive (the
 *     Bearer-auth blob-URL fetch — never `<img src="/api/...">`).
 *   - `FeedbackTonePill` mirrors `_feedbackTonePillHtml` +
 *     `FEEDBACK_NOTE_TYPE_LABELS` (the player-friendly tone
 *     labels — longer than the Coach Review composer's chips).
 */

/** Player-facing tone labels — same key set as `_VALID_NOTE_TYPES`. */
const TONE_LABELS: Record<string, string> = {
  positive: "Positive",
  correction: "Correction",
  question: "Question",
  team_concept: "Team concept",
  individual_goal: "Individual goal",
};

export function FeedbackTonePill({ noteType }: { noteType?: string | null }) {
  const label = noteType ? TONE_LABELS[noteType] : undefined;
  if (!label) return null;
  return (
    <span
      data-feedback-tone={noteType}
      data-testid="feedback-tone-pill"
      className="rounded px-1.5 py-0.5 text-[0.6rem] font-display uppercase tracking-wider bg-surface text-ink-muted"
    >
      {label}
    </span>
  );
}

/** Access-controlled thumbnail tile for a note or clip — the JPEG
 * via the M5 `useThumbnail` primitive; a film-strip placeholder
 * until it resolves (or on a 403/404 — a viewer who can't see the
 * source just gets the placeholder, never a broken image). */
export function FeedbackThumb({
  kind,
  id,
}: {
  kind: "note" | "clip";
  id: number;
}) {
  const url = useThumbnail(kind, id);
  if (!url) {
    return (
      <span
        className="block h-12 w-20 shrink-0 rounded bg-gradient-to-br from-canvas to-surface border border-rule/60"
        aria-hidden="true"
        data-testid="feedback-thumb-placeholder"
      />
    );
  }
  return (
    <img
      src={url}
      alt=""
      aria-hidden="true"
      data-testid="feedback-thumb"
      className="block h-12 w-20 shrink-0 rounded object-cover border border-rule/60"
    />
  );
}

/** A static placeholder tile for an observation note with no
 * tactical board — the clipboard glyph, no network request.
 * (An observation WITH a board renders a board chip instead;
 * that lands with the board-preview work, not this slice — for
 * now a board-bearing observation also shows this glyph.) */
export function ObservationThumb() {
  return (
    <span
      className="flex h-12 w-20 shrink-0 items-center justify-center rounded bg-surface border border-rule/60 text-ink-muted"
      aria-hidden="true"
      data-testid="feedback-observation-thumb"
    >
      <ClipboardList size={16} strokeWidth={2} aria-hidden="true" />
    </span>
  );
}
